// The relative-date token a generated app writes into its starting rows, and the one
// thing the gate and the publisher must agree on exactly.
//
// It lived in `saddleHost.ts`, which is the CLIENT publish orchestration and imports the
// project store and Cognito with it. The gate imported this one regex from there and
// inherited all of it — so the gate, which is pure and belongs anywhere, could only run
// where a signed-in app could. One regex was holding the whole pipeline in the client.

/**
 * The token grammar: an offset, then optionally a weekday, then optionally a time.
 *
 * The weekday part exists because the apps that need relative dates most are the ones
 * anchored to a day of the week — a wedding weekend is Friday drinks, Saturday ceremony,
 * Sunday brunch, and a supper club runs on Saturdays. Without it a generated wedding page
 * invented `'+2w 5fri 18:00'`, which parsed as nothing: publish passed the string through
 * untouched, `S.fmt.date` returned '' for it, and all three events rendered as a lone
 * separator where their date should have been.
 */
export const DATE_TOKEN = /^\+(\d{1,3})([dwh])(?:\s+(mon|tue|wed|thu|fri|sat|sun))?(?:\s+(\d{1,2}):(\d{2}))?$/i;

/**
 * How many days from publish a token lands — the floor of it.
 *
 * `null` when the string is not a token at all, so a caller can filter a row's values down
 * to the dated ones. Hours resolve within the same day, so they count as 0 — a '+3h' shift
 * is as good as gone by tomorrow, the same staleness a '+0d' one has.
 *
 * The weekday part ('+2w fri') pushes the date forward by up to six more days, by an amount
 * that depends on which weekday publish falls on — unknowable here, where there is no
 * publish date. So this returns the FLOOR (the offset alone), which is the safe direction
 * for a rule that asks "is this horizon too short": it never over-states how far ahead a
 * seed reaches, so it never quietly passes a schedule that is actually staler than it looks.
 */
export function tokenHorizonDays(value: string): number | null {
  const m = DATE_TOKEN.exec(value.trim());
  if (!m) return null;
  const amount = Number(m[1]);
  const unit = m[2].toLowerCase();
  if (unit === 'h') return 0;
  return amount * (unit === 'w' ? 7 : 1);
}

const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/**
 * The date a token lands on when published at `now`, or `null` when it is not a token.
 *
 * It lived inside the publisher (`resolveRelativeDates`), which is all that needed it until
 * the gate had to ask whether a row's end can land before its start. The two must resolve a
 * token identically — a gate that judged its own reading of '+3w sun' would pass rows the
 * publisher then writes backwards — so the one resolution lives here, beside the grammar.
 */
export function resolveDateToken(value: string, now: Date): Date | null {
  const m = DATE_TOKEN.exec(value.trim());
  if (!m) return null;
  const amount = Number(m[1]);
  const unit = m[2].toLowerCase();
  const when = new Date(now.getTime());
  if (unit === 'h') when.setHours(when.getHours() + amount);
  else when.setDate(when.getDate() + amount * (unit === 'w' ? 7 : 1));
  // A named day moves forward to the next such day from the offset, today included, so
  // '+2w fri' is never a day in the past. It is also why two named days in one row can
  // wrap: from a Tuesday, '+3w sun' is the day BEFORE '+3w mon'.
  if (m[3] !== undefined) {
    const target = DAYS.indexOf(m[3].toLowerCase() as (typeof DAYS)[number]);
    when.setDate(when.getDate() + ((target - when.getDay() + 7) % 7));
  }
  if (m[4] !== undefined) {
    when.setHours(Number(m[4]), Number(m[5]), 0, 0);
  }
  return when;
}
